import {useEffect, useRef, useState} from 'react';
import {Client, IFrame, IMessage} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

export interface AgentWorkflowStep {
    type: string;
    apiKeyId?: number;
    runId?: string;
    traceId?: string;
    sessionId?: string;
    decisionId?: string;
    turn?: number;
    stepType?: string;
    step?: string;
    functionName?: string;
    arguments?: string;
    result?: string;
    summary?: string;
    source?: string;
    outputType?: string;
    processingTimeMs?: number;
    timestamp: number;
}

interface AgentWebSocketState {
    connected: boolean;
    stepsByRun: Record<string, AgentWorkflowStep[]>;
    latestStep: AgentWorkflowStep | null;
}

export function useAgentWebSocket(apiKeyId: number | null, enabled: boolean = true): AgentWebSocketState {
    const [connected, setConnected] = useState(false);
    const [stepsByRun, setStepsByRun] = useState<Record<string, AgentWorkflowStep[]>>({});
    const [latestStep, setLatestStep] = useState<AgentWorkflowStep | null>(null);
    const clientRef = useRef<Client | null>(null);
    const clearTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const isSuccessTerminal = (data: AgentWorkflowStep): boolean =>
        data.functionName === 'output'
        || data.functionName === 'submit_final_output'
        || data.stepType === 'final_output'
        || data.stepType === 'text_output'
        || data.stepType === 'chat_output';

    const isFailureTerminal = (data: AgentWorkflowStep): boolean =>
        data.functionName === '__agent_failed__';

    useEffect(() => {
        if (!enabled || null === apiKeyId) {
            return;
        }

        const token = localStorage.getItem('auth_token');
        const client = new Client({
            webSocketFactory: () => new SockJS('/ws/chat'),
            connectHeaders: token ? {Authorization: `Bearer ${token}`} : {},
            debug: () => {
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
        });

        client.onConnect = (_frame: IFrame) => {
            setConnected(true);
            client.subscribe('/topic/bot/agent', (message: IMessage) => {
                try {
                    const data: AgentWorkflowStep = JSON.parse(message.body);
                    if (data.type === 'agent_workflow') {
                        // 只处理当前所选 API Key 的消息；后端事件消息未带 apiKeyId 时保留原行为
                        if (data.apiKeyId !== undefined && data.apiKeyId !== null
                            && Number(data.apiKeyId) !== apiKeyId) {
                            return;
                        }

                        const runId = data.runId || data.traceId || 'unknown';
                        setStepsByRun(prev => {
                            const runSteps = [...(prev[runId] || []), data].slice(-50);
                            return {...prev, [runId]: runSteps};
                        });
                        setLatestStep(data);

                        const clearRun = (delay: number) => {
                            const timers = clearTimersRef.current;
                            if (timers.has(runId)) {
                                clearTimeout(timers.get(runId)!);
                            }
                            timers.set(runId, setTimeout(() => {
                                setStepsByRun(prev => {
                                    const next = {...prev};
                                    delete next[runId];
                                    return next;
                                });
                                timers.delete(runId);
                            }, delay));
                        };

                        // Agent 执行完成时，5秒后仅清除该run的步骤
                        if (isSuccessTerminal(data)) {
                            clearRun(5000);
                        }
                        // Agent 执行失败时，3秒后清除该run的步骤
                        if (isFailureTerminal(data)) {
                            clearRun(3000);
                        }
                    }
                } catch {
                    // ignore malformed messages
                }
            });
        };

        client.onDisconnect = () => {
            setConnected(false);
        };

        client.onStompError = () => {
            setConnected(false);
        };

        client.activate();
        clientRef.current = client;

        return () => {
            clearTimersRef.current.forEach(timer => clearTimeout(timer));
            clearTimersRef.current.clear();
            client.deactivate();
            clientRef.current = null;
            setConnected(false);
            setStepsByRun({});
            setLatestStep(null);
        };
    }, [apiKeyId, enabled]);

    return {connected, stepsByRun, latestStep};
}
